import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Tags, Plus, Pencil, Trash2, Lock, Check, X as XIcon } from "lucide-react";
import { toast } from "sonner";

const ExpenseCategoryManager = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/expense-categories");
      setList(data);
    } catch {
      toast.error("Gagal memuat kategori");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const name = (newName || "").trim();
    if (!name) return;
    try {
      await api.post("/expense-categories", { name });
      toast.success("Kategori ditambahkan");
      setNewName("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menambah");
    }
  };

  const startEdit = (c) => { setEditingId(c.id); setEditingName(c.name); };
  const cancelEdit = () => { setEditingId(null); setEditingName(""); };

  const saveEdit = async () => {
    const name = (editingName || "").trim();
    if (!name) { toast.error("Nama kategori wajib diisi"); return; }
    try {
      await api.put(`/expense-categories/${editingId}`, { name });
      toast.success("Kategori diperbarui");
      cancelEdit();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal edit");
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/expense-categories/${confirmDel.id}`);
      toast.success("Kategori dihapus");
      setConfirmDel(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal hapus");
      setConfirmDel(null);
    }
  };

  return (
    <div className="bg-white border border-[#E4E4E7]" data-testid="expense-category-manager">
      <div className="px-6 py-4 border-b border-[#E4E4E7] flex items-center gap-2">
        <Tags size={14} />
        <div className="label-cap">Kategori Pengeluaran</div>
      </div>
      <div className="p-6">
        <p className="text-xs text-[#52525B] mb-4">Kelola daftar kategori yang bisa dipilih di menu Pengeluaran. Kategori terkunci (Upah) tidak bisa diedit/dihapus karena dipakai auto-post Payroll.</p>
        <form onSubmit={create} className="flex items-center gap-2 mb-4">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Nama kategori baru…"
            className="flex-1 px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] bg-white"
            data-testid="expcat-new-input"
          />
          <button type="submit" data-testid="expcat-add-button" className="bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold uppercase tracking-wider px-4 py-2 flex items-center gap-2">
            <Plus size={14} /> Tambah
          </button>
        </form>
        <div className="border border-[#E4E4E7]">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                <th className="text-left label-cap px-4 py-2">Nama Kategori</th>
                <th className="text-right label-cap px-4 py-2">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="2" className="p-4 text-sm text-[#52525B] text-center">Memuat…</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan="2" className="p-4 text-sm text-[#52525B] text-center">Belum ada kategori.</td></tr>}
              {list.map((c) => (
                <tr key={c.id} className="border-b border-[#E4E4E7]" data-testid={`expcat-row-${c.id}`}>
                  <td className="px-4 py-2 text-sm">
                    {editingId === c.id ? (
                      <input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                        autoFocus
                        className="px-2 py-1 border border-[#002FA7] text-sm outline-none bg-white w-full max-w-xs"
                        data-testid={`expcat-edit-input-${c.id}`}
                      />
                    ) : (
                      <span className="font-bold flex items-center gap-2">
                        {c.name}
                        {c.locked && (
                          <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-[#7C3AED]/10 text-[#7C3AED] border border-[#7C3AED]/30 px-1.5 py-0.5">
                            <Lock size={10} /> Terkunci
                          </span>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {editingId === c.id ? (
                      <div className="inline-flex gap-1">
                        <button onClick={saveEdit} data-testid={`expcat-save-${c.id}`} className="p-1.5 hover:bg-[#059669]/10 text-[#059669] border border-[#E4E4E7]"><Check size={14} /></button>
                        <button onClick={cancelEdit} data-testid={`expcat-cancel-${c.id}`} className="p-1.5 hover:bg-[#F4F4F5] border border-[#E4E4E7]"><XIcon size={14} /></button>
                      </div>
                    ) : (
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => startEdit(c)}
                          disabled={c.locked}
                          data-testid={`expcat-edit-${c.id}`}
                          className="p-1.5 hover:bg-[#F4F4F5] border border-[#E4E4E7] disabled:opacity-40 disabled:cursor-not-allowed"
                          title={c.locked ? "Kategori terkunci" : "Edit"}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => setConfirmDel(c)}
                          disabled={c.locked}
                          data-testid={`expcat-delete-${c.id}`}
                          className="p-1.5 hover:bg-[#DC2626]/10 text-[#DC2626] border border-[#E4E4E7] disabled:opacity-40 disabled:cursor-not-allowed"
                          title={c.locked ? "Kategori terkunci" : "Hapus"}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setConfirmDel(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E4E4E7] max-w-sm w-full p-6">
            <h3 className="font-heading text-lg font-bold mb-2">Hapus Kategori?</h3>
            <p className="text-sm text-[#52525B] mb-6">{confirmDel.name}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button onClick={remove} data-testid="confirm-delete-expcat" className="px-4 py-2 text-xs font-bold uppercase bg-[#DC2626] text-white">Hapus</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseCategoryManager;
